import { Injectable } from '@angular/core';

const STORAGE_PREFIX = 'gd_form_draft:';
const DRAFT_SCHEMA_VERSION = 1;
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredFormDraft {
  schemaVersion: number;
  savedAt: number;
  payload: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getStoredId = (key: string): string => {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (!isRecord(value)) return 'anonymous';
    const id = value['_id'] ?? value['id'];
    return typeof id === 'string' ? encodeURIComponent(id) : 'anonymous';
  } catch {
    return 'anonymous';
  }
};

export const clearStoredFormDrafts = (): void => {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    // Storage may be disabled by the browser; form entry must still work.
  }
};

@Injectable({ providedIn: 'root' })
export class FormDraftStorageService {
  public isReloadNavigation(): boolean {
    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return navigation?.type === 'reload';
  }

  public save<T>(scope: string, payload: T): boolean {
    const draft: StoredFormDraft = {
      schemaVersion: DRAFT_SCHEMA_VERSION,
      savedAt: Date.now(),
      payload,
    };

    try {
      sessionStorage.setItem(this.storageKey(scope), JSON.stringify(draft));
      return true;
    } catch {
      return false;
    }
  }

  public load<T>(
    scope: string,
    isPayload: (value: unknown) => value is T,
  ): T | null {
    const key = this.storageKey(scope);
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;

      const parsed: unknown = JSON.parse(raw);
      if (!isRecord(parsed)) {
        sessionStorage.removeItem(key);
        return null;
      }

      const savedAt = parsed['savedAt'];
      if (
        parsed['schemaVersion'] !== DRAFT_SCHEMA_VERSION ||
        typeof savedAt !== 'number' ||
        Date.now() - savedAt > MAX_DRAFT_AGE_MS ||
        !isPayload(parsed['payload'])
      ) {
        sessionStorage.removeItem(key);
        return null;
      }

      return parsed['payload'];
    } catch {
      sessionStorage.removeItem(key);
      return null;
    }
  }

  public clear(scope: string): void {
    try {
      sessionStorage.removeItem(this.storageKey(scope));
    } catch {
      // Storage may be disabled by the browser; form navigation must continue.
    }
  }

  private storageKey(scope: string): string {
    const companyId = getStoredId('company');
    const userId = getStoredId('user');
    return `${STORAGE_PREFIX}${companyId}:${userId}:${encodeURIComponent(scope)}`;
  }
}
